import { useShop } from '@/contexts/ShopContext';
import Character3D, { type CharacterConfig } from '../game/elements/Character3D';
import { PROPS_CATALOG } from '@/types/props';
import { useMemo } from 'react';

// Deep partial type helper
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

// Character configurations - should match CharacterShopSection
const CHARACTER_CONFIGS: Record<string, DeepPartial<CharacterConfig>> = {
  default_hero: {}, // Uses all defaults
  tiny_blue: {
    sizes: { characterSize: 0.5, cubeSize: 0.95 },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #2968a3 100%)',
        back: 'linear-gradient(135deg, #357abd 0%, #2968a3 50%, #1e5189 100%)',
        top: 'linear-gradient(135deg, #5ba0f2 0%, #4a90e2 50%, #357abd 100%)',
        bottom: 'linear-gradient(135deg, #2968a3 0%, #1e5189 50%, #143a5f 100%)',
        left: 'linear-gradient(135deg, #4080d0 0%, #3570c0 50%, #2a60b0 100%)',
        right: 'linear-gradient(135deg, #4585d5 0%, #3a75c5 50%, #2f65b5 100%)',
      },
    },
    face: { eyes: { size: '35%' } },
  },
  chonky_green: {
    sizes: { characterSize: 0.95, cubeSize: 0.85, shadow: { width: 0.9, height: 0.25, offsetY: 0.12 } },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)',
        back: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 50%, #1b5e20 100%)',
        top: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #43a047 100%)',
        bottom: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #0d3f15 100%)',
        left: 'linear-gradient(135deg, #43a047 0%, #3b9040 50%, #338037 100%)',
        right: 'linear-gradient(135deg, #48a84c 0%, #409843 50%, #38883b 100%)',
      },
    },
    face: { eyes: { size: '15%', gap: '35%' }, mouth: { width: '40%' } },
  },
  sleek_purple: {
    sizes: { characterSize: 0.75, cubeSize: 0.98 },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #9c27b0 0%, #8e24aa 50%, #7b1fa2 100%)',
        back: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
        top: 'linear-gradient(135deg, #ab47bc 0%, #9c27b0 50%, #8e24aa 100%)',
        bottom: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 50%, #2a0a5c 100%)',
        left: 'linear-gradient(135deg, #8e24aa 0%, #7e22a0 50%, #6e1f96 100%)',
        right: 'linear-gradient(135deg, #9125ac 0%, #8123a2 50%, #712098 100%)',
      },
      effects: { shadow: 'radial-gradient(ellipse, rgba(156, 39, 176, 0.4) 0%, transparent 70%)' },
    },
    face: {
      eyes: { size: '22%', shine: { size: '40%', position: { top: '15%', left: '20%' } } },
      mouth: { width: '30%', heights: { neutral: '12%', happy: '20%', shocked: '25%' } },
    },
  },
  golden_glow: {
    sizes: { characterSize: 0.8, cubeSize: 0.9 },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #ffc107 0%, #ffb300 50%, #ffa000 100%)',
        back: 'linear-gradient(135deg, #ffa000 0%, #ff8f00 50%, #ff6f00 100%)',
        top: 'linear-gradient(135deg, #ffca28 0%, #ffc107 50%, #ffb300 100%)',
        bottom: 'linear-gradient(135deg, #ff8f00 0%, #ff6f00 50%, #e65100 100%)',
        left: 'linear-gradient(135deg, #ffb300 0%, #ffa300 50%, #ff9300 100%)',
        right: 'linear-gradient(135deg, #ffb800 0%, #ffa800 50%, #ff9800 100%)',
      },
    },
    backFace: { symbol: '*', sizeScale: 0.4, opacity: 0.6 },
  },
};

const HomeCharacter: React.FC = () => {
  const { shopState } = useShop();
  
  // Get the equipped character config
  const equippedCharacterId = shopState.equippedCharacter || 'default_hero';
  const characterConfig = CHARACTER_CONFIGS[equippedCharacterId] || {};

  // Convert equipped prop IDs to actual prop objects
  const equippedProps = useMemo(() => {
    return (shopState.equippedProps || [])
      .map(propId => PROPS_CATALOG.find(prop => prop.id === propId))
      .filter((prop): prop is NonNullable<typeof prop> => prop !== undefined);
  }, [shopState.equippedProps]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100px',
        height: '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Character3D
        position={[0, 0]}
        direction={null}
        isMoving={false}
        cellSize={100}
        gameState="playing"
        config={characterConfig}
        equippedProps={equippedProps}
      />
    </div>
  );
};

export default HomeCharacter;
